import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, LinearProgress } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiCreateDocumentGenerationJob, ApiService, CompanyService, FileModel, OpenAPI } from '../../../../api';
import { request as __request } from '../../../../api/core/request';
import { RetrieveDocumentGenerationJob } from '../../../../api/models/RetrieveDocumentGenerationJob';
import { DOCUMENTS_BY_ID_QUERY_KEY } from '../../../../common/constants/services/documents';
import { EventType, useEventSubscription } from '../../../../context';
import { useAsyncProcessor } from '../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { useLayoutContext } from '../../../../context/LayoutContext';
import { useCitationContext } from '../../../../pages/CapTable/cap-table/components/captable-table/CitationContext';
import { PDFGenerationJobParsingMode } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-parsing-mode.enum';
import { GenerationJobState } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-state.enum';
import { useDocuments } from '../../../../services/hooks';
import { Logger } from '../../../../utilities';
import PDFDocumentSearch from '../../../FileViewer/pdf-viewer/pdf-document-search/PDFDocumentSearch';
import { SearchMatch } from '../../../FileViewer/pdf-viewer/pdf-document-search/PDFDocumentSearch.props';
import { PageData, PDFCitationPayload } from '../../../FileViewer/pdf-viewer/pdf-utils/constants';
import PDFViewer, { PDF_MAX_ZOOM, PDF_MIN_ZOOM, PDF_ZOOM_STEP } from '../../../FileViewer/pdf-viewer/PDFViewer';
import { PDFViewerViewMode, SearchHighlight } from '../../../FileViewer/pdf-viewer/PDFViewer.props';
import { isFindKeyboardShortcut, isScopedViewerKeyboardShortcut } from '../../../FileViewer/viewerKeyboard.utils';
import { useAttributeMappedWidgetDrawer } from '../../attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';
import { useDrawerContext } from '../../draggable-drawer/DraggableDrawer.context';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from '../document-preview.constants';
import {
  adoptLatestParsingJob,
  canStartCitableParse,
  createParsingJobWithConflictRetry,
  discoverParsingJob,
  isFailedParsingStatus,
  isInFlightParsingJob,
  isParserJsonReady,
  isParsingJob,
  jobsFromListResponse,
  PARSING_JOBS_PAGE_SIZE,
  PARSING_PROGRESS_COPY,
  resolveCitationParseDisplay,
  shouldAutoCreateParsingJob,
  shouldShowPreparingBanner,
} from './adoptParsingJob';
import { PDFJobRibbon } from './pdf-job-ribbon/PDFJobRibbon';
import PDFPageSelectionControls from './pdf-page-selection-controls/PDFPageSelectionControls';
import { PDFViewerTabProps } from './PDFViewerTab.props';
import { FileViewWithExternalJobId } from './PDFViewerTab.types';

const PDF_PAGE_SELECTION_MAX_ZOOM = 1.5;
const PDF_PAGE_SELECTION_MIN_ZOOM = 0.75;

/** One document's pending PARSING-conflict backoff, so unmount or a document switch can end it. */
type ParsingBackoffRun = { cancelled: boolean; timer?: ReturnType<typeof setTimeout>; abort?: () => void };

const listCompanyParsingJobsPage = (companyId: string, page: number, pageSize: number) =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/api/company/{company_id}/document-generation-jobs',
    path: { company_id: companyId },
    query: {
      page,
      page_size: pageSize,
      processing_type: RetrieveDocumentGenerationJob.processing_type.PARSING,
    },
  });

const getReferenceObjectTypeCode = (displayType?: string): string => {
  const typeMap: Record<string, string> = {
    'Cap Table': 'ct',
    Financials: 'fi',
    'Fund Ownership': 'fo',
    'Custom Breakpoints': 'cb',
    'External Value': 'ev',
    'Specified Share Values': 'ssv',
    'Fund Investment Ownership Summary': 'fio',
    'Market Adjustment': 'ma',
  };
  if (displayType && displayType.length <= 3 && displayType === displayType.toLowerCase()) {
    return displayType;
  }
  return typeMap[displayType || ''] || 'ct';
};

const PDFViewerTab = ({ document, companyId }: PDFViewerTabProps) => {
  const file = document.file as FileViewWithExternalJobId;
  const queryClient = useQueryClient();
  const { apiCompanyDocumentGenerationJobsNewCreate } = ApiService;
  const { currentMeasurementDate } = useLayoutContext();
  const { isPopped } = useDrawerContext();
  const { downloadDocumentContent } = useDocuments();
  const { addJob, removeJob } = useAsyncProcessor();
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [allowPageSelection, setAllowPageSelection] = useState(false);
  const [processingJob, setProcessingJob] = useState<RetrieveDocumentGenerationJob>();
  const [renderingJob, setRenderingJob] = useState<RetrieveDocumentGenerationJob>();
  const [genJobProcessing, setGenJobProcessing] = useState(false);
  const [renderJobProcessing, setRenderJobProcessing] = useState(false);
  const [parseBusy, setParseBusy] = useState(false);
  const [parseFailed, setParseFailed] = useState(false);

  const { activeCitation, activeCitationVariant, activeCitationNonce, setActiveCitation } = useCitationContext();
  const { onCitationPushed } = useAttributeMappedWidgetDrawer();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState<SearchHighlight | null>(null);
  const [pdfZoomScale, setPdfZoomScale] = useState(1);
  const [pageSelectionZoomScale, setPageSelectionZoomScale] = useState(1);
  const controlsRef = useRef<HTMLDivElement>(null);
  const viewerRootRef = useRef<HTMLDivElement>(null);
  const parsingBackoffRef = useRef<ParsingBackoffRun>({ cancelled: false });
  const jobAttempted = useRef(false);
  const parseGenerationRef = useRef(0);
  const isCurrentParseRun = (generation: number) => parseGenerationRef.current === generation;

  // Resolves false once this tab is gone, which stops the retry loop instead of stranding it.
  const waitThroughParsingBackoff = useCallback(
    (delayInMs: number) =>
      new Promise<boolean>(resolve => {
        const run = parsingBackoffRef.current;
        if (run.cancelled) {
          resolve(false);
          return;
        }
        run.abort = () => resolve(false);
        run.timer = setTimeout(() => resolve(!run.cancelled), delayInMs);
      }),
    []
  );

  const parsingJobProcessorId = useMemo(
    () => (document.file?.id ? `parsing-file-${document.file.id}` : null),
    [document.file?.id]
  );

  // Clear active citation when the spreadsheet active cell changes
  useEventSubscription(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, () => {
    setActiveCitation();
  });

  // Clear the active citation only when this tab shows a DIFFERENT document than the
  // citation targets. A review eyeball / citation-badge jump sets the citation for THIS
  // document one tick before the tab mounts on a cold open; clearing unconditionally on
  // mount wiped it before PDFViewer could scroll to and highlight it. Only the active
  // document tab is mounted at a time (TabDrawer renders just the selected tab), so
  // keying off activeCitation here never races a sibling tab clearing each other.
  useEffect(() => {
    if (activeCitation && activeCitation.document_reference !== document.document_reference_id) {
      setActiveCitation();
    }
  }, [activeCitation, setActiveCitation, document.document_reference_id]);

  // ESC to dismiss active citation
  useEffect(() => {
    const targetWindow = controlsRef.current?.ownerDocument?.defaultView ?? window;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeCitation) {
        setActiveCitation();
      }
    };
    targetWindow.addEventListener('keydown', handleKeyDown);
    return () => targetWindow.removeEventListener('keydown', handleKeyDown);
  }, [activeCitation, setActiveCitation, isPopped]);

  const { data, isLoading } = useQuery({
    queryKey: ['document', document.document_reference_id, 'content'],
    enabled: !!document.document_reference_id,
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
    queryFn: () => downloadDocumentContent(document),
  });

  const parserResultsQueryKey = useMemo(
    () => ['document', document.document_reference_id, 'parser-results'],
    [document.document_reference_id]
  );

  const {
    data: parserResultsData,
    isError: parserResultsError,
    refetch: refetchParserResults,
  } = useQuery({
    queryKey: parserResultsQueryKey,
    enabled: !!document.file?.json_file_path,
    staleTime: 0,
    queryFn: () => ApiService.apiDocumentsReferencesParserResultsRead(document.document_reference_id.toString()),
  });

  const parserJsonQueryKey = useMemo(
    () => ['document', document.document_reference_id, 'parser-json', parserResultsData?.url],
    [document.document_reference_id, parserResultsData?.url]
  );

  const {
    data: jsonData,
    isError: parserJsonError,
    refetch: refetchParserJson,
  } = useQuery({
    queryKey: parserJsonQueryKey,
    enabled: Boolean(parserResultsData?.url),
    staleTime: 0,
    queryFn: async () => {
      const response = await fetch(parserResultsData!.url as string);
      if (!response.ok) {
        throw new Error(`Failed to load parser JSON (${response.status})`);
      }
      const payload: unknown = await response.json();
      if (!isParserJsonReady(payload)) {
        throw new Error('Parser JSON is not an array');
      }
      return payload as PageData[];
    },
  });

  const handleParsingJobComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: parserResultsQueryKey });
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_BY_ID_QUERY_KEY] });
  }, [queryClient, parserResultsQueryKey]);

  const renderingJobRef = useRef<RetrieveDocumentGenerationJob | undefined>(undefined);
  renderingJobRef.current = renderingJob;

  const materializeCompletedParsingJob = useCallback(
    async (result: RetrieveDocumentGenerationJob): Promise<boolean> => {
      const generation = parseGenerationRef.current;
      try {
        if (!companyId || !currentMeasurementDate?.id) {
          return false;
        }
        await CompanyService.apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList(
          companyId,
          result.job_id,
          undefined,
          true,
          currentMeasurementDate.id,
          getReferenceObjectTypeCode(document.referenced_in?.[0]?.reference_object_type) as
            | 'ct'
            | 'fi'
            | 'fo'
            | 'cb'
            | 'ev'
            | 'ssv'
            | 'fio'
            | 'ma'
        );
        if (!isCurrentParseRun(generation)) {
          return false;
        }
        const parserResults = (await ApiService.apiDocumentsReferencesParserResultsRead(
          String(document.document_reference_id)
        )) as { url?: string } | undefined;
        if (!isCurrentParseRun(generation)) {
          return false;
        }
        if (!parserResults?.url) {
          return false;
        }
        queryClient.setQueryData(parserResultsQueryKey, parserResults);
        queryClient.invalidateQueries({ queryKey: [DOCUMENTS_BY_ID_QUERY_KEY] });
        return true;
      } catch (error) {
        Logger.error('Error creating file references from parsing job:', error);
        return false;
      }
    },
    [
      companyId,
      currentMeasurementDate?.id,
      document.document_reference_id,
      document.referenced_in,
      parserResultsQueryKey,
      queryClient,
    ]
  );

  const addParsingJobToProcessor = useCallback(
    (job: RetrieveDocumentGenerationJob) => {
      if (!parsingJobProcessorId) {
        return;
      }

      // This callback outlives the tab in the global processor. Capture the
      // document generation NOW; reading the ref when a poll finishes would
      // let an old job claim whichever PDF happens to be mounted then.
      const generation = parseGenerationRef.current;

      addJob({
        id: parsingJobProcessorId,
        details: {
          title: 'PDF Processing',
          description: 'AI Assisted PDF Processing',
        },
        pollingConfig: {
          pollingInterval: 3000,
          pollingArgs: [companyId, job.job_id],
          pollingFunction: CompanyService.apiCompanyDocumentGenerationJobsRead,
        },
        onResult: async (result: RetrieveDocumentGenerationJob) => {
          const failed = isFailedParsingStatus(result.status);
          const completed = result.status === RetrieveDocumentGenerationJob.status.COMPLETED;
          if (!isCurrentParseRun(generation)) {
            // Terminal stale jobs still need their global polling entry removed,
            // but must not touch this tab's state, backend materialization, or cache.
            if (failed || completed) {
              removeJob(parsingJobProcessorId);
            }
            return;
          }
          setRenderingJob(result);

          if (failed) {
            removeJob(parsingJobProcessorId);
            return;
          }

          if (!completed) {
            return;
          }

          removeJob(parsingJobProcessorId);
          const materialized = await materializeCompletedParsingJob(result);
          if (!isCurrentParseRun(generation)) {
            return;
          }
          const currentJob = renderingJobRef.current;
          if (currentJob && currentJob.job_id !== result.job_id && isInFlightParsingJob(currentJob)) {
            return;
          }
          if (!materialized) {
            setParseFailed(true);
          }
        },
        onError: err => {
          Logger.error('Error polling parsing job:', err);
        },
      });
    },
    [addJob, removeJob, parsingJobProcessorId, companyId, materializeCompletedParsingJob]
  );

  const handleCitationPushed = useCallback(
    (payload: PDFCitationPayload) => {
      onCitationPushed?.(payload, document.document_reference_id);
    },
    [onCitationPushed, document.document_reference_id]
  );

  const handleSearchMatchSelect = useCallback((match: SearchMatch) => {
    setSearchHighlight({
      pageNumber: match.pageNumber,
      wordIds: match.matchedWordIds,
      rects: match.rects,
    });
  }, []);

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    setSearchHighlight(null);
  }, []);

  const getPdfZoomBounds = useCallback(
    () => ({
      max: allowPageSelection ? PDF_PAGE_SELECTION_MAX_ZOOM : PDF_MAX_ZOOM,
      min: allowPageSelection ? PDF_PAGE_SELECTION_MIN_ZOOM : PDF_MIN_ZOOM,
    }),
    [allowPageSelection]
  );

  const handlePdfZoomOut = useCallback(() => {
    const { min } = getPdfZoomBounds();
    const setZoomScale = allowPageSelection ? setPageSelectionZoomScale : setPdfZoomScale;

    setZoomScale(prev => Math.max(min, prev - PDF_ZOOM_STEP));
  }, [allowPageSelection, getPdfZoomBounds]);

  const handlePdfZoomIn = useCallback(() => {
    const { max } = getPdfZoomBounds();
    const setZoomScale = allowPageSelection ? setPageSelectionZoomScale : setPdfZoomScale;

    setZoomScale(prev => Math.min(max, prev + PDF_ZOOM_STEP));
  }, [allowPageSelection, getPdfZoomBounds]);

  const handlePdfZoomReset = useCallback(() => {
    const setZoomScale = allowPageSelection ? setPageSelectionZoomScale : setPdfZoomScale;

    setZoomScale(1);
  }, [allowPageSelection]);

  useEffect(() => {
    if (!jsonData) return;

    // When portaled into the popout window, keyboard events fire on that
    // window's object, not the opener's. Resolve via the DOM element so
    // Ctrl+F works in both docked and popped-out modes.
    const viewerElement = viewerRootRef.current;
    const targetWindow = viewerElement?.ownerDocument?.defaultView ?? window;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !isFindKeyboardShortcut(e) ||
        !isScopedViewerKeyboardShortcut(viewerElement, e, { allowWhenViewerMounted: true })
      ) {
        return;
      }

      e.preventDefault();
      setSearchOpen(prev => !prev);
    };
    targetWindow.addEventListener('keydown', handleKeyDown);
    return () => targetWindow.removeEventListener('keydown', handleKeyDown);
  }, [jsonData, isPopped]);

  const handlePageToggle = (pageNumber: number) => {
    setSelectedPages(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(pageNumber)) {
        newSelected.delete(pageNumber);
      } else {
        newSelected.add(pageNumber);
      }
      return newSelected;
    });
  };

  const handleProcessDocument = useCallback(
    async (isTableExtraction: boolean = true) => {
      const generation = parseGenerationRef.current;
      if (!canStartCitableParse(document.file?.id, companyId)) {
        Logger.warn('Missing required data: file ID or company ID');
        jobAttempted.current = false;
        setParseFailed(true);
        setParseBusy(false);
        return;
      }

      try {
        if (isTableExtraction) {
          setGenJobProcessing(true);
        } else {
          setRenderJobProcessing(true);
          setParseBusy(true);
          setParseFailed(false);
        }

        const parserOverrides = isTableExtraction
          ? [
            {
              file_type: FileModel.file_type.PDF,
              page_numbers: Array.from(selectedPages),
              pdf_parsing_mode: PDFGenerationJobParsingMode.TABULAR,
              convert_parsed_results_to_xlsx: true,
            },
          ]
          : [
            {
              file_type: FileModel.file_type.PDF,
              page_numbers: [],
              pdf_parsing_mode: PDFGenerationJobParsingMode.CITABLE,
              convert_parsed_results_to_xlsx: false,
            },
          ];

        const postJob = () =>
          apiCompanyDocumentGenerationJobsNewCreate(companyId, {
            reference_files: [document.file.id!],
            processing_type: ApiCreateDocumentGenerationJob.processing_type.PARSING,
            desired_termination_state: GenerationJobState.PENDING_PROMPTING,
            parser_overrides: parserOverrides,
            metadata: {
              document_reference_id: document.document_reference_id,
              measurement_date_id: currentMeasurementDate?.id,
            },
          });

        if (isTableExtraction) {
          setProcessingJob(await postJob());
          return;
        }

        const created = await createParsingJobWithConflictRetry({
          postJob,
          findExistingJob: async () =>
            adoptLatestParsingJob(
              jobsFromListResponse(await listCompanyParsingJobsPage(companyId, 0, PARSING_JOBS_PAGE_SIZE)),
              document.document_reference_id,
              document.file?.guid
            ),
          wait: waitThroughParsingBackoff,
        });

        // A conflict that outlasts the backoff is not a failure: leave the manual retrigger available.
        if (created.outcome === 'gave-up') {
          Logger.warn(`Parsing job still locked after ${created.attempts} attempts; leaving the document unclaimed`);
          if (!isCurrentParseRun(generation)) {
            return;
          }
          setRenderJobProcessing(false);
          jobAttempted.current = false;
          setParseBusy(false);
          setParseFailed(true);
          return;
        }

        if (!isCurrentParseRun(generation)) {
          return;
        }
        setRenderingJob(created.job);
        addParsingJobToProcessor(created.job);
        setRenderJobProcessing(false);
        setParseBusy(false);
        queryClient.invalidateQueries({ queryKey: [DOCUMENTS_BY_ID_QUERY_KEY] });
      } catch (error) {
        Logger.error(`Error ${isTableExtraction ? 'processing pages' : 'rendering document'}:`, error);
        if (isTableExtraction) {
          setGenJobProcessing(false);
        } else {
          setRenderJobProcessing(false);
          jobAttempted.current = false;
          if (isCurrentParseRun(generation)) {
            setParseBusy(false);
            setParseFailed(true);
          }
        }
      }
    },
    [
      apiCompanyDocumentGenerationJobsNewCreate,
      companyId,
      document.file?.id,
      document.file?.guid,
      selectedPages,
      document.document_reference_id,
      currentMeasurementDate?.id,
      addParsingJobToProcessor,
      queryClient,
      waitThroughParsingBackoff,
    ]
  );

  const handleProcessPages = useCallback(() => handleProcessDocument(true), [handleProcessDocument]);
  const handleRenderDocument = useCallback(() => handleProcessDocument(false), [handleProcessDocument]);

  const handleClearSelection = () => {
    setSelectedPages(new Set());
  };

  const toggleSelectionMode = (enabled: boolean) => {
    setAllowPageSelection(enabled);
    if (!enabled) {
      setSelectedPages(new Set());
    }
  };

  const referenceObjectType = getReferenceObjectTypeCode(document.referenced_in?.[0]?.reference_object_type);

  useEffect(() => {
    parseGenerationRef.current += 1;
    const generation = parseGenerationRef.current;
    jobAttempted.current = false;
    setRenderingJob(undefined);
    setRenderJobProcessing(false);
    setProcessingJob(undefined);
    setParseBusy(false);
    setParseFailed(false);
    const run = parsingBackoffRef.current;
    return () => {
      if (parseGenerationRef.current === generation) {
        parseGenerationRef.current += 1;
      }
      run.cancelled = true;
      clearTimeout(run.timer);
      run.abort?.();
      parsingBackoffRef.current = { cancelled: false };
    };
  }, [document.document_reference_id]);

  const isCapTable = referenceObjectType === 'ct';
  const parserReady = isParserJsonReady(jsonData);
  const parserArtifactPresent = Boolean(file?.json_file_path || parserResultsData?.url);
  const jobInFlight = isInFlightParsingJob(renderingJob) || renderJobProcessing;
  const canReadFileJob = !!(file?.generation_job_external_id && companyId);
  const displayParsePhase = resolveCitationParseDisplay({
    parserReady,
    parserJsonError,
    parserResultsError,
    parserArtifactPresent,
    parseFailed,
    parseBusy,
    jobInFlight,
  });

  useEffect(() => {
    if (!isCapTable || parserReady || parserArtifactPresent || jobAttempted.current || jobInFlight) {
      return;
    }

    const requestDocumentId = document.document_reference_id;
    const generation = parseGenerationRef.current;
    const stillThisRun = () => isCurrentParseRun(generation);

    const attachInFlightParsingJob = (job: RetrieveDocumentGenerationJob) => {
      if (!stillThisRun()) {
        return true;
      }
      if (!isInFlightParsingJob(job)) {
        return false;
      }
      setRenderingJob(job);
      addParsingJobToProcessor(job);
      setParseBusy(false);
      return true;
    };

    const discoverAndMaybeCreate = () => {
      jobAttempted.current = true;
      setParseBusy(true);
      setParseFailed(false);
      discoverParsingJob({
        fetchPage: (page, pageSize) => listCompanyParsingJobsPage(companyId, page, pageSize),
        documentReferenceId: requestDocumentId,
        fileGuid: document.file?.guid,
        pageSize: PARSING_JOBS_PAGE_SIZE,
      })
        .then(async ({ adopted, listFailed }) => {
          if (!stillThisRun()) {
            return;
          }
          if (adopted && attachInFlightParsingJob(adopted)) {
            return;
          }
          if (adopted && isParsingJob(adopted) && adopted.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
            setRenderingJob(adopted);
            const materialized = await materializeCompletedParsingJob(adopted);
            if (!stillThisRun()) {
              return;
            }
            if (isInFlightParsingJob(renderingJobRef.current)) {
              return;
            }
            if (materialized) {
              setParseBusy(false);
              return;
            }
          }
          if (
            shouldAutoCreateParsingJob({
              parserReady,
              adoptedJob: isInFlightParsingJob(adopted) ? adopted : null,
              parserArtifactPresent,
            })
          ) {
            handleRenderDocument();
          } else {
            setParseBusy(false);
          }
          if (listFailed) {
            Logger.error('Failed to adopt parsing job: job list request failed; creating a CITABLE job', undefined);
          }
        })
        .catch(err => {
          Logger.error('Failed to adopt parsing job:', err);
          if (!stillThisRun()) {
            return;
          }
          if (shouldAutoCreateParsingJob({ parserReady, adoptedJob: null, parserArtifactPresent })) {
            handleRenderDocument();
          } else {
            jobAttempted.current = false;
            setParseBusy(false);
            setParseFailed(true);
          }
        });
    };

    if (canReadFileJob) {
      CompanyService.apiCompanyDocumentGenerationJobsRead(companyId, file.generation_job_external_id!)
        .then(job => {
          if (isParsingJob(job) && isInFlightParsingJob(job)) {
            jobAttempted.current = true;
            attachInFlightParsingJob(job);
            return;
          }
          discoverAndMaybeCreate();
        })
        .catch(err => {
          Logger.error('Failed to resume existing generation job:', err);
          discoverAndMaybeCreate();
        });
      return;
    }

    discoverAndMaybeCreate();
  }, [
    isCapTable,
    parserReady,
    parserArtifactPresent,
    jobInFlight,
    canReadFileJob,
    companyId,
    file?.generation_job_external_id,
    document.document_reference_id,
    document.file?.guid,
    addParsingJobToProcessor,
    materializeCompletedParsingJob,
    handleRenderDocument,
  ]);

  const currentDocumentHasActiveCitation = useMemo(
    () => activeCitation?.document_reference === document.document_reference_id,
    [activeCitation, document]
  );

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data.content && !data.blob_url) {
    return <div>No document found: Current Document Preview size limit is 5MB</div>;
  }

  const canRetriggerCitable = !renderJobProcessing && (!renderingJob || displayParsePhase === 'failed');
  const retryCitationParse = () => {
    jobAttempted.current = false;
    if (parserArtifactPresent) {
      refetchParserResults();
      refetchParserJson();
      return;
    }
    setRenderingJob(undefined);
    setParseFailed(false);
    setParseBusy(true);
    handleRenderDocument();
  };
  let citationProgress = null;
  if (displayParsePhase === 'failed') {
    citationProgress = (
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Alert
          severity="error"
          action={
            <button type="button" onClick={retryCitationParse}>
              Retry
            </button>
          }>
          Citation processing failed
        </Alert>
      </Box>
    );
  } else if (renderingJob) {
    citationProgress = (
      <PDFJobRibbon
        generationJob={renderingJob}
        measurementDate={currentMeasurementDate}
        referenceObjectType={referenceObjectType}
        onJobComplete={handleParsingJobComplete}
        skipPolling
        parserReady={parserReady}
        fileId={document.file?.id}
        onRetrigger={job => {
          if (parsingJobProcessorId) {
            removeJob(parsingJobProcessorId);
          }
          setRenderingJob(job);
          addParsingJobToProcessor(job);
        }}
      />
    );
  } else if (shouldShowPreparingBanner(displayParsePhase, parserReady)) {
    citationProgress = (
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ display: 'flex', alignItems: 'center' }}>
          {PARSING_PROGRESS_COPY.preparing}
        </Alert>
      </Box>
    );
  }
  const activePdfZoomScale = allowPageSelection ? pageSelectionZoomScale : pdfZoomScale;
  const { max: pdfZoomMax, min: pdfZoomMin } = getPdfZoomBounds();
  const pdfZoomPercentage = Math.round(activePdfZoomScale * 100);

  return (
    <Box
      ref={viewerRootRef}
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
        width: '100%',
      }}>
      <Box ref={controlsRef} sx={{ flexShrink: 0 }}>
        {processingJob && currentMeasurementDate && (
          <PDFJobRibbon
            generationJob={processingJob}
            measurementDate={currentMeasurementDate}
            referenceObjectType={referenceObjectType}
            parserReady={parserReady}
          />
        )}

        {citationProgress}

        <PDFPageSelectionControls
          allowPageSelection={allowPageSelection}
          isPopped={isPopped}
          selectedPages={selectedPages}
          onToggleSelectionMode={toggleSelectionMode}
          onPageToggle={handlePageToggle}
          onClearSelection={handleClearSelection}
          onProcessPages={handleProcessPages}
          genJobProcessing={genJobProcessing}
          canRetriggerCitable={canRetriggerCitable}
          handleRenderDocument={handleRenderDocument}
          hasJsonData={!!jsonData}
          onSearchToggle={() => setSearchOpen(prev => !prev)}
          searchOpen={searchOpen}
          zoomPercentage={pdfZoomPercentage}
          canZoomIn={activePdfZoomScale < pdfZoomMax}
          canZoomOut={activePdfZoomScale > pdfZoomMin}
          canResetZoom={activePdfZoomScale !== 1}
          onZoomIn={handlePdfZoomIn}
          onZoomOut={handlePdfZoomOut}
          onResetZoom={handlePdfZoomReset}
        />
      </Box>

      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          position: 'relative',
          overflow: 'hidden',
        }}>
        {searchOpen && jsonData && (
          <PDFDocumentSearch jsonData={jsonData} onMatchSelect={handleSearchMatchSelect} onClose={handleSearchClose} />
        )}

        <Box
          id="pdf-viewer-scroll-container"
          sx={{
            background: 'linear-gradient(180deg, #F3F8FD 0%, #EEF4F8 100%)',
            boxSizing: 'border-box',
            height: '100%',
            minHeight: 0,
            overscrollBehavior: 'contain',
            overflowX: allowPageSelection ? 'hidden' : 'auto',
            overflowY: 'auto',
            paddingBottom: '10px',
            paddingTop: '10px',
            scrollbarGutter: allowPageSelection ? 'auto' : 'stable both-edges',
            width: '100%',
          }}>
          <PDFViewer
            base64Content={data.content}
            src={data.blob_url}
            allowPageSelection={allowPageSelection}
            selectedPages={selectedPages}
            viewMode={allowPageSelection ? PDFViewerViewMode.THUMBNAIL : PDFViewerViewMode.DOCUMENT}
            onPageToggle={handlePageToggle}
            jsonData={jsonData ?? undefined}
            activeCitation={currentDocumentHasActiveCitation ? activeCitation : undefined}
            activeCitationVariant={activeCitationVariant}
            activeCitationNonce={activeCitationNonce}
            onCitationPushed={handleCitationPushed}
            searchHighlight={searchHighlight}
            zoomScale={activePdfZoomScale}
            onZoomScaleChange={allowPageSelection ? setPageSelectionZoomScale : setPdfZoomScale}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PDFViewerTab;
